import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { aiEnrich, aiProofread } from '../services/api';
import DiffText from './DiffText';

const REASON_KEYS = {
  not_configured: 'ai.errConfig',
  auth: 'ai.errAuth',
  quota: 'ai.errQuota',
  timeout: 'ai.errTimeout',
  error: 'ai.errGeneric',
  bad_provider: 'ai.errGeneric',
  bad_model: 'ai.errModel',
};

export function aiErrorKey(reason) {
  return REASON_KEYS[reason] || 'ai.errGeneric';
}

/** Preview panel shown after an AI suggestion — Replace / Ignore. */
export function AiPreview({ text, original, onApply, onDiscard, applyLabel, title }) {
  const { t } = useTranslation();
  return (
    <div className="ai-preview">
      <div className="ai-preview-head">✨ {title || t('ai.suggestion')}</div>
      {/* A correction is shown against the original, differences highlighted;
          an enrichment rewrites the whole text, so it is shown as is. */}
      {original != null
        ? <DiffText before={original} after={text} />
        : <div className="ai-preview-body">{text}</div>}
      {/* The expert signs the report: nothing the AI wrote is a finding
          until they have checked it. */}
      <p className="ai-preview-check">{t('ai.checkFacts')}</p>
      <div className="ai-preview-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={onApply}>
          {applyLabel || t('ai.replace')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onDiscard}>
          {t('ai.ignore')}
        </button>
      </div>
    </div>
  );
}

/**
 * AI helpers for a single text field: "Enrich" rewrites and expands a short
 * summary; "Proofread" only fixes spelling, grammar and phrasing. Both go
 * through a preview — nothing is overwritten until the user accepts.
 *
 * NOTE: render this with a `key` tied to the field it edits. Without one React
 * keeps the same instance when the surrounding field changes (switching report
 * steps, for instance), and a pending suggestion would be applied to the wrong
 * field.
 */
export default function AiEnrich({ reportId, kind, value, onApply, disabled }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');        // '' | 'enrich' | 'proofread'
  const [result, setResult] = useState(null);  // { action, text }
  const [error, setError] = useState('');

  const run = async (action) => {
    setError('');
    setResult(null);
    setBusy(action);
    try {
      const payload = { kind, text: value || '' };
      const { text } =
        action === 'proofread'
          ? await aiProofread(reportId, payload)
          : await aiEnrich(reportId, payload);
      setResult({ action, text });
    } catch (e) {
      setError(t(aiErrorKey(e.response?.data?.detail)));
    } finally {
      setBusy('');
    }
  };

  const empty = !(value || '').trim();
  const locked = disabled || !!busy || empty;
  // A proofread that changed nothing is a result, not a no-op: say so rather
  // than offering a "suggestion" identical to what is already in the field.
  const unchanged =
    result?.action === 'proofread' &&
    (result.text || '').trim() === (value || '').trim();

  return (
    <div className="ai-enrich">
      <button
        type="button"
        className="btn btn-ghost btn-sm ai-btn"
        onClick={() => run('enrich')}
        disabled={locked}
        title={empty ? t('ai.enrichEmptyHint') : t('ai.enrichHint')}
      >
        {busy === 'enrich' ? t('ai.working') : `✨ ${t('ai.enrich')}`}
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-sm ai-btn"
        onClick={() => run('proofread')}
        disabled={locked}
        title={empty ? t('ai.enrichEmptyHint') : t('ai.proofreadHint')}
      >
        {busy === 'proofread' ? t('ai.working') : `✓ ${t('ai.proofread')}`}
      </button>
      {error && <span className="ai-error">{error}</span>}
      {unchanged && <span className="ai-ok">{t('ai.proofreadNoChange')}</span>}
      {result && !unchanged && (
        <AiPreview
          text={result.text}
          original={result.action === 'proofread' ? (value || '') : undefined}
          title={result.action === 'proofread' ? t('ai.proofreadResult') : t('ai.suggestion')}
          onApply={() => {
            onApply(result.text);
            setResult(null);
          }}
          onDiscard={() => setResult(null)}
        />
      )}
    </div>
  );
}
